/**
 * Speaker colours, shared so a speaker reads as the same colour on web and
 * mobile. The hue is derived from the speaker's id, never from their position
 * in the transcript, so filtering or reordering cannot reassign colours.
 *
 * Web sets the hue as a CSS variable and lets OKLCH do the rest. React Native
 * has no OKLCH, so `speakerColorHex` resolves the same OKLCH values to sRGB
 * here and both platforms land on one colour.
 */
import { SPEAKER_PALETTE, type ModeValues } from "@speakai/shared";

/**
 * Stable hue (0-359) for a speaker. Numeric ids spread around the wheel by the
 * golden angle, so adjacent speakers stay far apart and the wheel never runs
 * out. Other ids fall back to a character-code hash. The same id always yields
 * the same hue.
 */
export function speakerHue(speakerId: string | number | null | undefined): number {
  const id = String(speakerId ?? "").trim();
  const ordinal = /^\d+$/.test(id)
    ? Number(id)
    : id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return Math.round((ordinal * SPEAKER_PALETTE.hueStep) % 360);
}

/** `#rrggbb` for a speaker in a colour scheme, resolved from the same OKLCH values web uses; dark is lighter so a mark stays readable on a dark background. */
export function speakerColorHex(
  speakerId: string | number | null | undefined,
  scheme: keyof ModeValues<number> = "light",
): string {
  return oklchToHex(SPEAKER_PALETTE.lightness[scheme], SPEAKER_PALETTE.chroma, speakerHue(speakerId));
}

/**
 * `#rrggbb` for an OKLCH colour. A colour outside sRGB has its chroma reduced
 * until it fits, the way CSS Color 4 requires, because clamping each channel
 * on its own shifts the hue instead and the speaker would no longer match web.
 */
export function oklchToHex(lightness: number, chroma: number, hue: number): string {
  if (!Number.isFinite(lightness) || !Number.isFinite(chroma) || !Number.isFinite(hue)) {
    return oklchToHex(SPEAKER_PALETTE.lightness.light, SPEAKER_PALETTE.chroma, 0);
  }
  const [red, green, blue] = oklchToLinearRgb(lightness, fitChroma(lightness, chroma, hue), hue);
  return `#${channelToHex(red)}${channelToHex(green)}${channelToHex(blue)}`;
}

/** Largest chroma up to the requested one that keeps all three channels in sRGB. */
function fitChroma(lightness: number, chroma: number, hue: number): number {
  if (inGamut(oklchToLinearRgb(lightness, chroma, hue))) return chroma;
  let low = 0;
  let high = chroma;
  for (let i = 0; i < 24; i++) {
    const mid = (low + high) / 2;
    if (inGamut(oklchToLinearRgb(lightness, mid, hue))) low = mid;
    else high = mid;
  }
  return low;
}

function inGamut([red, green, blue]: [number, number, number]): boolean {
  const epsilon = 1e-6;
  return [red, green, blue].every((c) => c >= -epsilon && c <= 1 + epsilon);
}

function oklchToLinearRgb(
  lightness: number,
  chroma: number,
  hue: number,
): [number, number, number] {
  const radians = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(radians);
  const b = chroma * Math.sin(radians);

  const lCube = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mCube = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sCube = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
    -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
    -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
  ];
}

function channelToHex(linear: number): string {
  const gamma = linear <= 0.0031308 ? 12.92 * linear : 1.055 * linear ** (1 / 2.4) - 0.055;
  const clamped = Math.min(1, Math.max(0, gamma));
  return Math.round(clamped * 255)
    .toString(16)
    .padStart(2, "0");
}
